import { useEffect, useState } from "react";
import { api } from "../api";
import type { Run, Tier } from "../../server/types";

type ReplicaStatus = Awaited<ReturnType<typeof api.replicaStatus>>;

function fmtSize(bytes: number | null | undefined): string {
  if (bytes == null) return "—";
  const units = ["B", "KB", "MB", "GB", "TB"];
  let v = bytes; let i = 0;
  while (v >= 1024 && i < units.length - 1) { v /= 1024; i++; }
  return `${v.toFixed(v >= 100 ? 0 : 1)} ${units[i]}`;
}

function fmtAgo(ms: number): string {
  const d = Date.now() - ms;
  const s = Math.floor(d / 1000);
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60); if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60); if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

export default function DashboardPage() {
  const [replica, setReplica] = useState<ReplicaStatus | null>(null);
  const [runs, setRuns] = useState<Run[]>([]);
  const [triggering, setTriggering] = useState<Tier | null>(null);
  const [err, setErr] = useState<string | null>(null);

  async function refresh() {
    try {
      const [r, rs] = await Promise.all([api.replicaStatus(), api.getRuns(10)]);
      setReplica(r); setRuns(rs.runs);
    } catch (e) { setErr((e as Error).message); }
  }
  useEffect(() => { void refresh(); const t = setInterval(refresh, 5000); return () => clearInterval(t); }, []);

  async function trigger(tier: Tier) {
    setTriggering(tier); setErr(null);
    try {
      const r = await api.trigger(tier);
      if (!r.accepted) setErr(r.reason ?? "could not start backup");
      void refresh();
    } catch (e) { setErr((e as Error).message); }
    finally { setTriggering(null); }
  }

  const lastSuccess = runs.find((r) => r.status === "success");
  const lastFail = runs.find((r) => r.status === "failed");

  return (
    <>
      <h2>Dashboard</h2>

      {err && <div className="error-box" style={{ marginBottom: 16 }}>{err}</div>}

      <div className="grid-3">
        <div className="card">
          <h3>Replica</h3>
          {!replica && <div className="muted">checking…</div>}
          {replica && !replica.configured && (
            <div className="muted">Not configured. Open <a href="/settings">Settings</a>.</div>
          )}
          {replica && replica.configured && replica.reachable && (
            <div className="kv">
              <div className="k">Status</div>
              <div>
                <span className={`badge ${replica.withinThreshold ? "success" : "failed"}`}>
                  {replica.withinThreshold ? "healthy" : "lag exceeded"}
                </span>
              </div>
              <div className="k">Lag</div>
              <div>{replica.lagSeconds.toFixed(2)}s (threshold {replica.lagThresholdSeconds}s)</div>
            </div>
          )}
          {replica && replica.configured && !replica.reachable && (
            <div className="error-box">unreachable: {replica.error}</div>
          )}
        </div>

        <div className="card">
          <h3>Last successful run</h3>
          {!lastSuccess && <div className="muted">none yet</div>}
          {lastSuccess && (
            <div className="kv">
              <div className="k">Tier</div><div><span className={`badge ${lastSuccess.tier}`}>{lastSuccess.tier}</span></div>
              <div className="k">When</div><div>{fmtAgo(lastSuccess.started_at)}</div>
              <div className="k">Size</div><div>{fmtSize(lastSuccess.bytes_uploaded)}</div>
            </div>
          )}
        </div>

        <div className="card">
          <h3>Last failure</h3>
          {!lastFail && <div className="muted">none in recent history 🎉</div>}
          {lastFail && (
            <div className="kv">
              <div className="k">Tier</div><div><span className={`badge ${lastFail.tier}`}>{lastFail.tier}</span></div>
              <div className="k">When</div><div>{fmtAgo(lastFail.started_at)}</div>
              <div className="k">Error</div><div title={lastFail.error ?? ""}>{(lastFail.error ?? "").slice(0, 100)}</div>
            </div>
          )}
        </div>
      </div>

      <div className="card">
        <h3>Run a backup now</h3>
        <p className="muted">Manual trigger. Creates a run in the same way as the scheduler.</p>
        <div className="row">
          <button className="primary" disabled={!!triggering} onClick={() => trigger("daily")}>
            {triggering === "daily" ? "Starting…" : "Run daily"}
          </button>
          <button disabled={!!triggering} onClick={() => trigger("weekly")}>
            {triggering === "weekly" ? "Starting…" : "Run weekly"}
          </button>
          <button disabled={!!triggering} onClick={() => trigger("monthly")}>
            {triggering === "monthly" ? "Starting…" : "Run monthly"}
          </button>
        </div>
      </div>
    </>
  );
}
